import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useLinkVndlyPositionMutation } from 'queries/admin/vndly/useLinkVndlyPositionMutation'
import { useVndlyPositionIssuesPaginationQueryState } from '../hooks/useVndlyPositionIssuesPaginationQueryState'
import { useVndlyPositionIssuesQuery } from 'queries/admin/vndly/useVndlyPositionIssuesQuery'
import { useVndlyIssuesFiltersQueryState } from 'pages/VndlyIssues/hooks/useVndlyIssuesFiltersQueryState'

type VndlyPositionIssuesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useVndlyPositionIssuesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
    linkVndlyPositionMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<
      typeof useVndlyPositionIssuesPaginationQueryState
    >[1]
    linkVndlyPosition: ReturnType<typeof useLinkVndlyPositionMutation>['mutate']
    resetLinkVndlyPosition: ReturnType<
      typeof useLinkVndlyPositionMutation
    >['reset']
  }
}

const [Context, useVndlyPositionIssuesContextValue] =
  createContext<VndlyPositionIssuesContextState>({
    name: 'VndlyPositionIssuesContext',
  })

export const VndlyPositionIssuesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters] = useVndlyIssuesFiltersQueryState()

  const [pagination, setPagination] =
    useVndlyPositionIssuesPaginationQueryState()

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useVndlyPositionIssuesQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    instanceIds: filters.instanceIds,
    companyIds: filters.companyIds,
  })

  const {
    mutate: linkVndlyPosition,
    isPending: isLinkVndlyPositionPending,
    isError: isLinkVndlyPositionError,
    isSuccess: isLinkVndlyPositionSuccess,
    error: linkVndlyPositionError,
    reset: resetLinkVndlyPosition,
  } = useLinkVndlyPositionMutation()

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    // eslint-disable-next-line no-console
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
      linkVndlyPositionMutationState: {
        isPending: isLinkVndlyPositionPending,
        isError: isLinkVndlyPositionError,
        isSuccess: isLinkVndlyPositionSuccess,
        errorMessage: parseErrorMessage(linkVndlyPositionError),
      },
    },
    actions: {
      refetch,
      setPagination,
      linkVndlyPosition,
      resetLinkVndlyPosition,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useVndlyPositionIssuesContextValue }
